import React, { useState, useRef, useEffect } from 'react';
import apiClient from '../api/client';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  TrendingUp,
  Calculate,
  ShowChart,
  Add,
  Delete,
  Save,
  Upload,
  Download,
  ContentCopy,
  ChevronRight,
  ExpandMore,
} from '@mui/icons-material';
import { useModulePermission } from '../utils/useModulePermission';


interface ProjectionSimulationPageProps {
  startDate: string | null;
  endDate: string | null;
}

// Month columns (standard P&L months) - Default for FY2025-26
const STANDARD_MONTHS = ['Apr-25', 'May-25', 'Jun-25', 'Jul-25', 'Aug-25', 'Sep-25', 'Oct-25', 'Nov-25', 'Dec-25', 'Jan-26', 'Feb-26', 'Mar-26'];

// Helper function to generate months for a given FY (Indian calendar: Apr to Mar)
const generateFYMonths = (fyYear: string): string[] => {
  // Parse FY: "FY2025-26" → startYear = 2025, endYear = 2026
  const fyMatch = fyYear.match(/^FY(\d{4})-(\d{2})$/);
  if (!fyMatch) {
    return STANDARD_MONTHS; // Fallback to default
  }
  
  const startYear = parseInt(fyMatch[1]);
  const endYear = parseInt('20' + fyMatch[2]); // Convert 26 to 2026
  
  const months = [
    `Apr-${startYear.toString().slice(-2)}`,  // Apr-25
    `May-${startYear.toString().slice(-2)}`,  // May-25
    `Jun-${startYear.toString().slice(-2)}`,  // Jun-25
    `Jul-${startYear.toString().slice(-2)}`,  // Jul-25
    `Aug-${startYear.toString().slice(-2)}`,  // Aug-25
    `Sep-${startYear.toString().slice(-2)}`,  // Sep-25
    `Oct-${startYear.toString().slice(-2)}`,  // Oct-25
    `Nov-${startYear.toString().slice(-2)}`,  // Nov-25
    `Dec-${startYear.toString().slice(-2)}`,  // Dec-25
    `Jan-${endYear.toString().slice(-2)}`,    // Jan-26
    `Feb-${endYear.toString().slice(-2)}`,     // Feb-26
    `Mar-${endYear.toString().slice(-2)}`     // Mar-26
  ];
  
  return months;
};

// Helper function to auto-detect current FY based on current date
const getCurrentFY = (): string => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11 (Jan=0, Dec=11)
  const currentYear = now.getFullYear();
  
  // Indian FY: Apr to Mar
  // If current month is Jan-Mar (0-2), FY is previous year to current year
  // If current month is Apr-Dec (3-11), FY is current year to next year
  let startYear: number;
  let endYear: number;
  
  if (currentMonth < 3) { // Jan, Feb, Mar
    startYear = currentYear - 1;
    endYear = currentYear;
  } else { // Apr onwards
    startYear = currentYear;
    endYear = currentYear + 1;
  }
  
  return `FY${startYear}-${endYear.toString().slice(-2)}`;
};

// Expanded P&L structure (matching Finance Tab structure)
const EXPANDED_PNL_STRUCTURE = [
  // Revenue Section
  { name: 'Revenue', level: 0, isTotal: true, isCalculated: false },
  { name: 'India', level: 1, isTotal: true, isCalculated: false },
  { name: 'US', level: 1, isTotal: true, isCalculated: false },
  
  // Cost of Sales Section
  { name: 'Delivery Costs', level: 0, isTotal: true, isCalculated: false },
  { name: 'Software /Other Tech Subscriptions', level: 1, isTotal: false, isCalculated: false },
  { name: 'Subcontracting/Direct Resourcing', level: 1, isTotal: false, isCalculated: false },
  { name: 'Delivery Cost- Resources', level: 1, isTotal: false, isCalculated: false },
  { name: 'Bench Costs Resources', level: 1, isTotal: false, isCalculated: false },
  { name: 'Bench/Unallocated Cost- Resources', level: 1, isTotal: false, isCalculated: false },
  
  // Gross Profit Section
  { name: 'Gross profit/Variable Margin', level: 0, isTotal: false, isCalculated: true },
  { name: 'GP ratio', level: 0, isTotal: false, isCalculated: true },
  
  // Commission/Marketing Cost
  { name: 'Commission/Marketing Cost', level: 0, isTotal: false, isCalculated: false },
  
  // SG&A Section
  { name: 'SG & A expenses', level: 0, isTotal: true, isCalculated: false },
  { name: 'Corporate staff cost', level: 1, isTotal: true, isCalculated: false },
  { name: 'Sales Staff Costs', level: 2, isTotal: false, isCalculated: false },
  { name: 'Marketing Staff Costs', level: 2, isTotal: false, isCalculated: false },
  { name: 'HR Staff Costs', level: 2, isTotal: false, isCalculated: false },
  { name: 'Finance & Admin Staff Costs', level: 2, isTotal: false, isCalculated: false },
  { name: 'Staff Welfare Costs', level: 1, isTotal: false, isCalculated: false },
  { name: 'Director remuneration', level: 1, isTotal: false, isCalculated: false },
  { name: 'Rent', level: 1, isTotal: false, isCalculated: false },
  { name: 'Office Adminstration Cost', level: 1, isTotal: false, isCalculated: false },
  { name: 'Other subscriptions', level: 1, isTotal: false, isCalculated: false },
  { name: 'Travel', level: 1, isTotal: false, isCalculated: false },
  { name: 'Event', level: 1, isTotal: false, isCalculated: false },
  { name: 'Business Promotion', level: 1, isTotal: false, isCalculated: false },
  { name: 'Professional/Consultancy Fees', level: 1, isTotal: false, isCalculated: false },
  { name: 'Rates and Taxes', level: 1, isTotal: false, isCalculated: false },
  { name: 'Insurance', level: 1, isTotal: false, isCalculated: false },
  
  // EBITDA Section
  { name: 'EBITDA (without extraordinary items)', level: 0, isTotal: false, isCalculated: true },
  { name: 'Expenses not related to operations', level: 0, isTotal: false, isCalculated: false },
  { name: 'EBITDA (after extraordinary items)', level: 0, isTotal: false, isCalculated: true },
  
  // Depreciation Section
  { name: 'Depreciation & Amortization', level: 0, isTotal: false, isCalculated: false },
  
  // EBIT Section
  { name: 'EBIT', level: 0, isTotal: false, isCalculated: true },
  
  // Other Income Section
  { name: 'Other Income', level: 0, isTotal: false, isCalculated: false },
  { name: 'Interest Income', level: 0, isTotal: false, isCalculated: false },
  
  // Finance Charges Section
  { name: 'Finance Charges/Forex', level: 0, isTotal: true, isCalculated: false },
  { name: 'Bank Charges', level: 1, isTotal: false, isCalculated: false },
  { name: 'Interest Cost- OD', level: 1, isTotal: false, isCalculated: false },
  { name: 'Interest Cost- Car and Vehicle Expense', level: 1, isTotal: false, isCalculated: false },
  { name: 'Forex (gain)/loss', level: 1, isTotal: false, isCalculated: false },
  
  // PBT Section
  { name: 'PBT', level: 0, isTotal: false, isCalculated: true },
  
  // Tax Section
  { name: 'Tax Charges', level: 0, isTotal: false, isCalculated: false },
  { name: 'Rates and Taxes', level: 0, isTotal: false, isCalculated: false },
  
  // PAT Section
  { name: 'PAT', level: 0, isTotal: false, isCalculated: true },
];

// Sample items for each sub-sheet (for display structure)
const REVENUE_ITEMS = [
  { name: 'India', isCategory: true },
  { name: 'Autoliv', isCategory: false },
  { name: 'Rina', isCategory: false },
  { name: 'MovieOne', isCategory: false },
  { name: 'Nephrocare', isCategory: false },
  { name: 'Open Assessment', isCategory: false },
  { name: 'Others', isCategory: false },
  { name: 'US', isCategory: true },
  { name: 'Slim', isCategory: false },
  { name: 'Sequencer', isCategory: false },
];

const COST_OF_SALES_ITEMS = [
  { name: 'Delivery Cost- Resources', isCategory: true },
  { name: 'Employee 1', isCategory: false },
  { name: 'Employee 2', isCategory: false },
  { name: 'Bench Costs Resources', isCategory: true },
  { name: 'Bench Employee 1', isCategory: false },
  { name: 'Subcontracting/Direct Resourcing', isCategory: true },
  { name: 'Vendor 1', isCategory: false },
  { name: 'Software /Other Tech Subscriptions', isCategory: true },
  { name: 'Software 1', isCategory: false },
  { name: 'Bench/Unallocated Cost- Resources', isCategory: true },
  { name: 'Bench Employee 1', isCategory: false },
];

const SGA_ITEMS = [
  { name: 'Corporate staff cost', isCategory: true },
  { name: 'Sales Staff Costs', isCategory: true },
  { name: 'Sales Employee 1', isCategory: false },
  { name: 'Marketing Staff Costs', isCategory: true },
  { name: 'Marketing Employee 1', isCategory: false },
  { name: 'HR Staff Costs', isCategory: true },
  { name: 'HR Employee 1', isCategory: false },
  { name: 'Finance & Admin Staff Costs', isCategory: true },
  { name: 'Finance Employee 1', isCategory: false },
  { name: 'Staff Welfare Costs', isCategory: true },
  { name: 'Director remuneration', isCategory: false },
  { name: 'Rent', isCategory: false },
  { name: 'Office Adminstration Cost', isCategory: false },
  { name: 'Other subscriptions', isCategory: true },
  { name: 'Travel', isCategory: true },
  { name: 'Event', isCategory: true },
  { name: 'Business Promotion', isCategory: true },
  { name: 'Professional/Consultancy Fees', isCategory: true },
  { name: 'Rates and Taxes', isCategory: false },
  { name: 'Insurance', isCategory: false },
];

const FINANCE_ITEMS = [
  { name: 'Commission/Marketing Cost', isCategory: false },
  { name: 'Depreciation & Amortization', isCategory: false },
  { name: 'Other Income', isCategory: false },
  { name: 'Interest Income', isCategory: false },
  { name: 'Finance Charges/Forex', isCategory: true },
  { name: 'Bank Charges', isCategory: false },
  { name: 'Interest Cost- OD', isCategory: false },
  { name: 'Interest Cost- Car and Vehicle Expense', isCategory: false },
  { name: 'Forex (gain)/loss', isCategory: false },
  { name: 'Tax Charges', isCategory: false },
];

const ProjectionSimulationPage: React.FC<ProjectionSimulationPageProps> = ({
  startDate,
  endDate,
}) => {
  const { can } = useModulePermission('Projection Simulation');
  const [activeMainTab, setActiveMainTab] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [selectedScenario, setSelectedScenario] = useState<string>('');
  const [selectedFY, setSelectedFY] = useState<string>(getCurrentFY());
  const [availableFYs, setAvailableFYs] = useState<string[]>([]);
  const [loadingFYs, setLoadingFYs] = useState(false);
  const [months, setMonths] = useState<string[]>(generateFYMonths(getCurrentFY()));
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [cellValues, setCellValues] = useState<{ [key: string]: string }>({});
  const [draggingCell, setDraggingCell] = useState<{ row: number; col: number } | null>(null);
  const [dragTarget, setDragTarget] = useState<{ row: number; col: number } | null>(null);
  const [copySource, setCopySource] = useState<{ row: number; col: number; value: string } | null>(null);
  const [revenueItems, setRevenueItems] = useState(REVENUE_ITEMS);
  const [costOfSalesItems, setCostOfSalesItems] = useState(COST_OF_SALES_ITEMS);
  const [sgaItems, setSgaItems] = useState(SGA_ITEMS);
  const [financeItems, setFinanceItems] = useState(FINANCE_ITEMS);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [scenarios, setScenarios] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [scenarioError, setScenarioError] = useState<string | null>(null);
  const [addRowDialogOpen, setAddRowDialogOpen] = useState(false);
  const [addRowCategoryIndex, setAddRowCategoryIndex] = useState<number | null>(null);
  const [newRowName, setNewRowName] = useState('');
  const [isAddingHeader, setIsAddingHeader] = useState(false);
  const [addFYDialogOpen, setAddFYDialogOpen] = useState(false);
  const [newFYYear, setNewFYYear] = useState('');
  const [newFYStartYear, setNewFYStartYear] = useState('');
  const [newFYEndYear, setNewFYEndYear] = useState('');
  const [changeFYDialogOpen, setChangeFYDialogOpen] = useState(false);
  const [newFYToSet, setNewFYToSet] = useState<string>('');
  const [copyFromFY, setCopyFromFY] = useState<string>('');
  // Track expanded/collapsed state for categories (key: "sheet-rowIndex", value: boolean)
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const shouldRestoreScrollRef = useRef<boolean>(false);
  // Store source type (Cashflow/Investment) for each row (not category)
  const [rowSources, setRowSources] = useState<{ [key: string]: 'Cashflow' | 'Investment' }>({});
  // Store department for each row in Cost of Sales and SG&A
  const [rowDepartments, setRowDepartments] = useState<{ [key: string]: string }>({});
  
  // Callback ref to set the table container
  const setTableContainerRef = (node: HTMLDivElement | null) => {
    tableContainerRef.current = node;
  };

  const handleMainTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveMainTab(newValue);
    // Reset sub-tab when switching main tabs
    if (newValue === 0) {
      setActiveSubTab(0);
    }
  };

  const handleSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveSubTab(newValue);
  };

  // Clear any existing errors on mount
  useEffect(() => {
    setScenarioError(null);
  }, []);

  // Load financial years from API
  useEffect(() => {
    const loadFinancialYears = async () => {
      setLoadingFYs(true);
      setScenarioError(null); // Clear any previous errors
      try {
        const response = await apiClient.get('/api/v1/projection-simulation/financial-years?active_only=true');
        const fyList = response.data.financial_years?.map((fy: any) => fy.fy_year) || [];
        
        // If no FYs returned, add default ones
        if (fyList.length === 0) {
          const currentFY = getCurrentFY();
          const defaultFYs = [currentFY];
          // Add next 2 FYs
          const currentYear = new Date().getFullYear();
          const currentMonth = new Date().getMonth();
          let startYear = currentMonth < 3 ? currentYear - 1 : currentYear;
          for (let i = 1; i <= 2; i++) {
            const nextStartYear = startYear + i;
            const nextEndYear = nextStartYear + 1;
            defaultFYs.push(`FY${nextStartYear}-${nextEndYear.toString().slice(-2)}`);
          }
          setAvailableFYs(defaultFYs);
          setSelectedFY((prev) => (prev && defaultFYs.includes(prev) ? prev : currentFY));
        } else {
          setAvailableFYs(fyList);
          // If selected FY is not in the list, use first available or current FY
          if (fyList.length > 0) {
            setSelectedFY((prev) => (prev && fyList.includes(prev) ? prev : fyList[0]));
          }
        }
      } catch (error: any) {
        // Fallback to default FYs if API fails
        const currentFY = getCurrentFY();
        const defaultFYs = [currentFY];
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        let startYear = currentMonth < 3 ? currentYear - 1 : currentYear;
        for (let i = 1; i <= 2; i++) {
          const nextStartYear = startYear + i;
          const nextEndYear = nextStartYear + 1;
          defaultFYs.push(`FY${nextStartYear}-${nextEndYear.toString().slice(-2)}`);
        }
        setAvailableFYs(defaultFYs);
        setSelectedFY((prev) => (prev && defaultFYs.includes(prev) ? prev : currentFY));
      } finally {
        setLoadingFYs(false);
      }
    };

    loadFinancialYears();
  }, []);

  // Update months when selected FY changes
  useEffect(() => {
    setMonths(generateFYMonths(selectedFY));
  }, [selectedFY]);

  // Load scenarios from API
  useEffect(() => {
    const loadScenarios = async () => {
      setLoadingScenarios(true);
      setScenarioError(null);
      try {
        const response = await apiClient.get('/api/v1/projection-simulation/scenarios');
        setScenarios(response.data.scenarios || []);
        setScenarioError(null); // Clear any previous errors on success
      } catch (error: any) {
        // Check for network errors in multiple ways
        const isNetworkError = 
          error.code === 'ERR_NETWORK' || 
          error.message?.includes('Network Error') ||
          error.message?.includes('network') ||
          (error.response === undefined && error.request !== undefined);
        
        if (isNetworkError) {
          // Don't show network errors - they're usually temporary (backend not running, CORS, etc.)
          setScenarioError(null);
        } else {
          const errorMessage = error.response?.data?.detail || error.message || 'Failed to load scenarios';
          // Only show meaningful errors, not generic network errors
          if (errorMessage && !errorMessage.toLowerCase().includes('network')) {
            setScenarioError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
          } else {
            setScenarioError(null);
          }
        }
      } finally {
        setLoadingScenarios(false);
      }
    };

    loadScenarios();
  }, []);

  // Load scenario data when scenario is selected
  useEffect(() => {
    if (!selectedScenario || selectedScenario === 'new') {
      return;
    }

    const loadScenarioData = async () => {
      try {
        const response = await apiClient.get(`/api/v1/projection-simulation/scenarios/${selectedScenario}`);
        const data = response.data;
        
        // Update selected FY to match scenario's financial_year
        if (data.scenario?.financial_year) {
          setSelectedFY(data.scenario.financial_year);
        }
        
        // Populate cell values from loaded data
        const newCellValues: { [key: string]: string } = {};
        
        // Revenue data
        data.revenue?.forEach((item: any) => {
          if (item.month && item.amount) {
            const rowIdx = revenueItems.findIndex(r => r.name === item.item_name);
            const colIdx = months.indexOf(item.month);
            if (rowIdx >= 0 && colIdx >= 0) {
              newCellValues[`${rowIdx + 1000}-${colIdx}`] = item.amount.toString();
            }
          }
        });
        
        // Cost of Sales data
        data.cost_of_sales?.forEach((item: any) => {
          if (item.month && item.amount) {
            const rowIdx = costOfSalesItems.findIndex(r => r.name === item.item_name);
            const colIdx = months.indexOf(item.month);
            if (rowIdx >= 0 && colIdx >= 0) {
              newCellValues[`${rowIdx + 2000}-${colIdx}`] = item.amount.toString();
            }
          }
        });
        
        // SG&A data
        data.sga?.forEach((item: any) => {
          if (item.month && item.amount) {
            const rowIdx = sgaItems.findIndex(r => r.name === item.item_name);
            const colIdx = months.indexOf(item.month);
            if (rowIdx >= 0 && colIdx >= 0) {
              newCellValues[`${rowIdx + 3000}-${colIdx}`] = item.amount.toString();
            }
          }
        });
        
        // Finance data
        data.finance?.forEach((item: any) => {
          if (item.month && item.amount) {
            const rowIdx = financeItems.findIndex(r => r.name === item.item_name);
            const colIdx = months.indexOf(item.month);
            if (rowIdx >= 0 && colIdx >= 0) {
              newCellValues[`${rowIdx + 4000}-${colIdx}`] = item.amount.toString();
            }
          }
        });
        
        setCellValues(newCellValues);
        setScenarioError(null); // Clear error on success
      } catch (error: any) {
        // Check for network errors in multiple ways
        const isNetworkError = 
          error.code === 'ERR_NETWORK' || 
          error.message?.includes('Network Error') ||
          error.message?.includes('network') ||
          (error.response === undefined && error.request !== undefined);
        
        if (isNetworkError) {
          setScenarioError(null);
        } else {
          const errorMessage = error.response?.data?.detail || error.message || 'Failed to load scenario data';
          // Only show meaningful errors, not generic network errors
          if (errorMessage && !errorMessage.toLowerCase().includes('network')) {
            setScenarioError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
          } else {
            setScenarioError(null);
          }
        }
      }
    };

    loadScenarioData();
  }, [selectedScenario, revenueItems, costOfSalesItems, sgaItems, financeItems, months]);

  // Restore scroll position after items change (when adding rows)
  useEffect(() => {
    if (shouldRestoreScrollRef.current && scrollPositionRef.current > 0 && tableContainerRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (tableContainerRef.current) {
              tableContainerRef.current.scrollTop = scrollPositionRef.current;
              shouldRestoreScrollRef.current = false; // Reset flag after restoring
            }
          });
        });
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [revenueItems, costOfSalesItems, sgaItems, financeItems]);

  // Save data to backend (immediate save)
  const saveToDatabase = async (immediate: boolean = false) => {
    if (!selectedScenario) return;
    
    // Clear existing timeout if not immediate
    if (!immediate && saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    setSaveStatus('saving');
    
    const performSave = async () => {
      try {
        // Collect all data from current state
        const allData: any[] = [];
        
        // Revenue data
        revenueItems.forEach((item, idx) => {
          months.forEach((month, colIdx) => {
            const val = getCellValue(idx + 1000, colIdx);
            if (val) {
              allData.push({
                scenario_id: parseInt(selectedScenario),
                sheet_type: 'revenue',
                item_name: item.name,
                month: month,
                amount: parseFloat(val) || 0,
                is_category: item.isCategory,
                row_index: idx,
                parent_category: null
              });
            }
          });
        });
        
        // Cost of Sales data
        costOfSalesItems.forEach((item, idx) => {
          months.forEach((month, colIdx) => {
            const val = getCellValue(idx + 2000, colIdx);
            if (val) {
              allData.push({
                scenario_id: parseInt(selectedScenario),
                sheet_type: 'cost_of_sales',
                item_name: item.name,
                month: month,
                amount: parseFloat(val) || 0,
                is_category: item.isCategory,
                row_index: idx,
                parent_category: null
              });
            }
          });
        });
        
        // SG&A data
        sgaItems.forEach((item, idx) => {
          months.forEach((month, colIdx) => {
            const val = getCellValue(idx + 3000, colIdx);
            if (val) {
              allData.push({
                scenario_id: parseInt(selectedScenario),
                sheet_type: 'sga',
                item_name: item.name,
                month: month,
                amount: parseFloat(val) || 0,
                is_category: item.isCategory,
                row_index: idx,
                parent_category: null
              });
            }
          });
        });
        
        // Finance data
        financeItems.forEach((item, idx) => {
          months.forEach((month, colIdx) => {
            const val = getCellValue(idx + 4000, colIdx);
            if (val) {
              allData.push({
                scenario_id: parseInt(selectedScenario),
                sheet_type: 'finance',
                item_name: item.name,
                month: month,
                amount: parseFloat(val) || 0,
                is_category: item.isCategory,
                row_index: idx,
                parent_category: null
              });
            }
          });
        });
        
        // Save to backend
        await apiClient.post(`/api/v1/projection-simulation/scenarios/${selectedScenario}/data/bulk-save`, {
          data: allData
        });
        
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (error) {
        setSaveStatus('error');
      }
    };

    if (immediate) {
      // Immediate save (for row additions/deletions)
      await performSave();
    } else {
      // Debounced save (for cell edits)
      const timeout = setTimeout(performSave, 2000);
      setSaveTimeout(timeout);
    }
  };

  // Auto-save functionality (debounced)
  const triggerAutoSave = () => {
    saveToDatabase(false);
  };

  const handleAddRowUnderCategory = (categoryIndex: number) => {
    // Only allow adding rows in editable tabs (not Aggregated P&L)
    if (activeSubTab === 0) {
      return;
    }
    
    // Open dialog to ask if user wants to add Sub Header or Sub Row
    setAddRowCategoryIndex(categoryIndex);
    setNewRowName('');
    setIsAddingHeader(false);
    setAddRowDialogOpen(true);
  };

  const handleConfirmAddRow = async () => {
    if (!newRowName.trim() || addRowCategoryIndex === null) {
      return;
    }

    // Save current scroll position before making changes
    if (tableContainerRef.current) {
      scrollPositionRef.current = tableContainerRef.current.scrollTop;
      shouldRestoreScrollRef.current = true; // Set flag to restore scroll after state update
    }
    
    const categoryIdx = addRowCategoryIndex;
    let items: Array<{ name: string; isCategory: boolean }>;
    
    // Get the correct items array based on active tab
    switch (activeSubTab) {
      case 1:
        items = revenueItems;
        break;
      case 2:
        items = costOfSalesItems;
        break;
      case 3:
        items = sgaItems;
        break;
      case 4:
        items = financeItems;
        break;
      default:
        return;
    }
    
    if (categoryIdx >= items.length) {
      return;
    }

    // Find the position to insert
    let insertIndex: number;
    
    if (isAddingHeader) {
      // For Sub Header: Insert AFTER all items under this category (after all non-category items until next category)
      insertIndex = categoryIdx + 1;
      while (insertIndex < items.length && !items[insertIndex].isCategory) {
        insertIndex++;
      }
      // Now insertIndex points to the next category or end of list
    } else {
      // For Sub Row: Insert right after the category
      insertIndex = categoryIdx + 1;
    }
    
    // Create new item
    const newItem = {
      name: newRowName.trim(),
      isCategory: isAddingHeader
    };

    // Insert the new item
    switch (activeSubTab) {
      case 1:
        const newRevenueItems = [...revenueItems];
        newRevenueItems.splice(insertIndex, 0, newItem);
        setRevenueItems(newRevenueItems);
        break;
      case 2:
        const newCostItems = [...costOfSalesItems];
        newCostItems.splice(insertIndex, 0, newItem);
        setCostOfSalesItems(newCostItems);
        break;
      case 3:
        const newSgaItems = [...sgaItems];
        newSgaItems.splice(insertIndex, 0, newItem);
        setSgaItems(newSgaItems);
        break;
      case 4:
        const newFinanceItems = [...financeItems];
        newFinanceItems.splice(insertIndex, 0, newItem);
        setFinanceItems(newFinanceItems);
        break;
    }

    // Close dialog and reset (this will trigger the useEffect to restore scroll)
    setAddRowDialogOpen(false);
    setAddRowCategoryIndex(null);
    setNewRowName('');
    setIsAddingHeader(false);

    // Immediately save to database
    await saveToDatabase(true);
  };

  const handleCancelAddRow = () => {
    setAddRowDialogOpen(false);
    setAddRowCategoryIndex(null);
    setNewRowName('');
    setIsAddingHeader(false);
  };

  // Restore default US category and its items
  const restoreUSCategory = async () => {
    const currentItems = [...revenueItems];
    
    // Check if US already exists
    const usIndex = currentItems.findIndex(item => item.name === 'US' && item.isCategory);
    
    if (usIndex === -1) {
      // US doesn't exist, find where to insert it (after India and all its items)
      let insertIndex = currentItems.length;
      
      // Find India category
      const indiaIndex = currentItems.findIndex(item => item.name === 'India' && item.isCategory);
      if (indiaIndex !== -1) {
        // Find the end of India's items (next category or end of list)
        insertIndex = indiaIndex + 1;
        while (insertIndex < currentItems.length && !currentItems[insertIndex].isCategory) {
          insertIndex++;
        }
      }
      
      // Insert US category and its default items
      const usItems = [
        { name: 'US', isCategory: true },
        { name: 'Slim', isCategory: false },
        { name: 'Sequencer', isCategory: false },
      ];
      
      currentItems.splice(insertIndex, 0, ...usItems);
      setRevenueItems(currentItems);
    } else {
      // US exists, check if it has its default items
      let nextIndex = usIndex + 1;
      const existingItems: string[] = [];
      
      // Collect existing items under US
      while (nextIndex < currentItems.length && !currentItems[nextIndex].isCategory) {
        existingItems.push(currentItems[nextIndex].name);
        nextIndex++;
      }
      
      // Add missing default items
      const defaultItems = ['Slim', 'Sequencer'];
      const missingItems = defaultItems.filter(item => !existingItems.includes(item));
      
      if (missingItems.length > 0) {
        const itemsToAdd = missingItems.map(name => ({ name, isCategory: false }));
        currentItems.splice(nextIndex, 0, ...itemsToAdd);
        setRevenueItems(currentItems);
      }
    }
    
    // Immediately save to database after restoration
    await saveToDatabase(true);
  };

  const handleRemoveRow = async (rowIndex: number) => {
    // Only allow removing rows in editable tabs (not Aggregated P&L)
    if (activeSubTab === 0) {
      return;
    }

    let items: Array<{ name: string; isCategory: boolean }>;
    switch (activeSubTab) {
      case 1:
        items = revenueItems;
        break;
      case 2:
        items = costOfSalesItems;
        break;
      case 3:
        items = sgaItems;
        break;
      case 4:
        items = financeItems;
        break;
      default:
        return;
    }

    const itemToDelete = items[rowIndex];
    if (!itemToDelete) {
      return;
    }

    // For category items, check if there are child items and confirm deletion
    if (itemToDelete.isCategory) {
      // Count items under this category
      let childCount = 0;
      let nextIndex = rowIndex + 1;
      while (nextIndex < items.length && !items[nextIndex].isCategory) {
        childCount++;
        nextIndex++;
      }

      const confirmMessage = childCount > 0
        ? `Delete category "${itemToDelete.name}"?\n\nWARNING: This will also delete ${childCount} item(s) under it. All data will be permanently removed.`
        : `Delete category "${itemToDelete.name}"?`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }

      // Delete category and all its children
      const deleteCount = childCount + 1; // category + children
      
      switch (activeSubTab) {
        case 1:
          const newRevenueItems = [...revenueItems];
          newRevenueItems.splice(rowIndex, deleteCount);
          setRevenueItems(newRevenueItems);
          break;
        case 2:
          const newCostItems = [...costOfSalesItems];
          newCostItems.splice(rowIndex, deleteCount);
          setCostOfSalesItems(newCostItems);
          break;
        case 3:
          const newSgaItems = [...sgaItems];
          newSgaItems.splice(rowIndex, deleteCount);
          setSgaItems(newSgaItems);
          break;
        case 4:
          const newFinanceItems = [...financeItems];
          newFinanceItems.splice(rowIndex, deleteCount);
          setFinanceItems(newFinanceItems);
          break;
      }
    } else {
      // For non-category items, simple deletion
      if (!window.confirm(`Delete "${itemToDelete.name}"?`)) {
        return;
      }

      switch (activeSubTab) {
        case 1:
          const newRevenueItems = [...revenueItems];
          newRevenueItems.splice(rowIndex, 1);
          setRevenueItems(newRevenueItems);
          break;
        case 2:
          const newCostItems = [...costOfSalesItems];
          newCostItems.splice(rowIndex, 1);
          setCostOfSalesItems(newCostItems);
          break;
        case 3:
          const newSgaItems = [...sgaItems];
          newSgaItems.splice(rowIndex, 1);
          setSgaItems(newSgaItems);
          break;
        case 4:
          const newFinanceItems = [...financeItems];
          newFinanceItems.splice(rowIndex, 1);
          setFinanceItems(newFinanceItems);
          break;
      }
    }

    // Immediately save to database after deletion
    await saveToDatabase(true);
  };

  const handleEditRowName = (rowIndex: number, newName: string) => {
    triggerAutoSave(); // Save after editing row name
    switch (activeSubTab) {
      case 1:
        const newRevenueItems = [...revenueItems];
        newRevenueItems[rowIndex].name = newName;
        setRevenueItems(newRevenueItems);
        break;
      case 2:
        const newCostItems = [...costOfSalesItems];
        newCostItems[rowIndex].name = newName;
        setCostOfSalesItems(newCostItems);
        break;
      case 3:
        const newSgaItems = [...sgaItems];
        newSgaItems[rowIndex].name = newName;
        setSgaItems(newSgaItems);
        break;
      case 4:
        const newFinanceItems = [...financeItems];
        newFinanceItems[rowIndex].name = newName;
        setFinanceItems(newFinanceItems);
        break;
    }
  };

  const getIndentation = (level: number) => {
    return level * 24; // 24px per level
  };

  // Get cell value
  const getCellValue = (row: number, col: number) => {
    const key = `${row}-${col}`;
    return cellValues[key] || '';
  };

  // Set cell value
  const setCellValue = (row: number, col: number, value: string) => {
    const key = `${row}-${col}`;
    setCellValues({ ...cellValues, [key]: value });
  };

  // Handle cell edit
  const handleCellEdit = (row: number, col: number, value: string) => {
    setCellValue(row, col, value);
    triggerAutoSave(); // Trigger auto-save on edit
  };

  // Handle drag start (for copy)
  const handleDragStart = (e: React.DragEvent, row: number, col: number) => {
    const value = getCellValue(row, col);
    if (value) {
      setCopySource({ row, col, value });
      e.dataTransfer.effectAllowed = 'copy';
      setDraggingCell({ row, col });
    }
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, row: number, col: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragTarget({ row, col });
  };

  // Handle drop (copy value)
  const handleDrop = (e: React.DragEvent, row: number, col: number) => {
    e.preventDefault();
    if (copySource) {
      setCellValue(row, col, copySource.value);
      setCopySource(null);
      setDraggingCell(null);
      setDragTarget(null);
    }
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggingCell(null);
    setDragTarget(null);
    setCopySource(null);
  };

  // Financial Year handlers
  const handleFYChange = async (newFY: string) => {
    if (!selectedScenario || selectedScenario === 'new') {
      // Just update FY if no scenario selected
      setSelectedFY(newFY);
      return;
    }

    // If scenario is selected, prompt user to choose source FY for data copy
    setNewFYToSet(newFY);
    setChangeFYDialogOpen(true);
  };

  const handleConfirmFYChange = async () => {
    if (!selectedScenario || selectedScenario === 'new' || !newFYToSet) {
      return;
    }

    try {
      const params: any = { new_fy: newFYToSet };
      if (copyFromFY) {
        params.copy_from_fy = copyFromFY;
      }

      await apiClient.put(
        `/api/v1/projection-simulation/scenarios/${selectedScenario}/financial-year`,
        null,
        { params }
      );

      setSelectedFY(newFYToSet);
      setChangeFYDialogOpen(false);
      setNewFYToSet('');
      setCopyFromFY('');

      // Reload scenario data
      await apiClient.get(`/api/v1/projection-simulation/scenarios/${selectedScenario}`);
      // ... reload logic will be handled by existing useEffect
    } catch (error: any) {
      // Check for network errors
      const isNetworkError = 
        error.code === 'ERR_NETWORK' || 
        error.message?.includes('Network Error') ||
        error.message?.includes('network') ||
        (error.response === undefined && error.request !== undefined);
      
      if (isNetworkError) {
        setScenarioError(null);
      } else {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to change financial year';
        if (errorMessage && !errorMessage.toLowerCase().includes('network')) {
          setScenarioError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
        } else {
          setScenarioError(null);
        }
      }
    }
  };

  const handleAddFY = () => {
    setAddFYDialogOpen(true);
    setNewFYYear('');
    setNewFYStartYear('');
    setNewFYEndYear('');
  };

  const handleConfirmAddFY = async () => {
    // Validate FY format
    const fyMatch = newFYYear.match(/^FY(\d{4})-(\d{2})$/);
    if (!fyMatch) {
      setScenarioError('Invalid FY format. Expected format: FY2025-26');
      return;
    }

    const startYear = parseInt(fyMatch[1]);
    const endYear = parseInt('20' + fyMatch[2]);

    // Validate years match
    if (newFYStartYear && parseInt(newFYStartYear) !== startYear) {
      setScenarioError(`Start year must be ${startYear}`);
      return;
    }
    if (newFYEndYear && parseInt(newFYEndYear) !== endYear) {
      setScenarioError(`End year must be ${endYear}`);
      return;
    }

    try {
      await apiClient.post('/api/v1/projection-simulation/financial-years', {
        fy_year: newFYYear,
        start_year: startYear,
        end_year: endYear,
        is_active: true
      });

      // Add to available FYs and select it
      setAvailableFYs([...availableFYs, newFYYear]);
      setSelectedFY(newFYYear);
      setAddFYDialogOpen(false);
      setNewFYYear('');
      setNewFYStartYear('');
      setNewFYEndYear('');
      setScenarioError(null);
    } catch (error: any) {
      // Check for network errors
      const isNetworkError = 
        error.code === 'ERR_NETWORK' || 
        error.message?.includes('Network Error') ||
        error.message?.includes('network') ||
        (error.response === undefined && error.request !== undefined);
      
      if (isNetworkError) {
        setScenarioError(null);
      } else {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to add financial year';
        if (errorMessage && !errorMessage.toLowerCase().includes('network')) {
          setScenarioError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
        } else {
          setScenarioError(null);
        }
      }
    }
  };

  // Toggle expand/collapse for a category
  const toggleCategoryExpand = (sheetType: string, rowIndex: number) => {
    const key = `${sheetType}-${rowIndex}`;
    setExpandedCategories(prev => ({
      ...prev,
      [key]: !prev[key] // Toggle: if undefined, default to true (expanded)
    }));
  };

  // Check if a category is expanded (default to true if not set)
  const isCategoryExpanded = (sheetType: string, rowIndex: number): boolean => {
    const key = `${sheetType}-${rowIndex}`;
    return expandedCategories[key] !== false; // Default to expanded
  };

  // Filter items based on expanded state (handles nested categories)
  const getFilteredItems = (items: Array<{ name: string; isCategory: boolean }>, sheetType: string): Array<{ name: string; isCategory: boolean; originalIndex: number }> => {
    const filtered: Array<{ name: string; isCategory: boolean; originalIndex: number }> = [];
    let parentCategoryIndex: number | null = null;
    let parentCategoryExpanded = true;
    let currentSubCategoryIndex: number | null = null;
    let currentSubCategoryExpanded = true;

    items.forEach((item, index) => {
      if (item.isCategory) {
        const prevItem = index > 0 ? items[index - 1] : null;
        
        // Determine if this is a sub-category:
        // If we have a parent category, and either:
        // 1. Previous item is also a category (immediate sub-category), OR
        // 2. Previous item is not a category but we're still within parent's scope
        //    (i.e., we haven't seen a clear break indicating a new top-level category)
        let isSubCategory = false;
        if (parentCategoryIndex !== null) {
          if (prevItem?.isCategory === true) {
            // Previous item is a category - this is a sub-category
            isSubCategory = true;
          } else if (prevItem && !prevItem.isCategory) {
            // Previous item is not a category - check if we're still within parent scope
            // Look back to see if we're still under the parent (no clear break)
            // A clear break would be: we've seen items that don't belong to any sub-category
            // For now, assume if we have a parent and previous was an item, it's still a sub-category
            // unless we can detect otherwise
            isSubCategory = true; // Assume sub-category unless proven otherwise
          }
        }
        
        // Special case: if this category name matches known sub-categories under "Corporate staff cost",
        // treat it as a sub-category. Otherwise, if it's a known top-level category, it's a new parent.
        const knownSubCategories = ['Sales Staff Costs', 'Marketing Staff Costs', 'HR Staff Costs', 'Finance & Admin Staff Costs'];
        const knownTopLevelCategories = ['Staff Welfare Costs', 'Director remuneration', 'Rent', 'Office Adminstration Cost', 
                                         'Other subscriptions', 'Travel', 'Event', 'Business Promotion', 
                                         'Professional/Consultancy Fees', 'Rates and Taxes', 'Insurance'];
        
        if (parentCategoryIndex !== null) {
          if (knownSubCategories.includes(item.name)) {
            // This is definitely a sub-category under Corporate staff cost
            isSubCategory = true;
          } else if (knownTopLevelCategories.includes(item.name)) {
            // This is a known top-level category - we've left the parent
            isSubCategory = false;
          }
          // Otherwise, use the previous logic
        }
        
        if (!isSubCategory) {
          // This is a top-level category (new parent)
          parentCategoryIndex = index;
          parentCategoryExpanded = isCategoryExpanded(sheetType, index);
          currentSubCategoryIndex = null;
          currentSubCategoryExpanded = true;
          // Always include category headers
          filtered.push({ ...item, originalIndex: index });
        } else {
          // This is a sub-category under a parent category
          if (parentCategoryExpanded) {
            currentSubCategoryIndex = index;
            currentSubCategoryExpanded = isCategoryExpanded(sheetType, index);
            // Include sub-category header if parent is expanded
            filtered.push({ ...item, originalIndex: index });
          } else {
            // Parent is collapsed, reset sub-category
            currentSubCategoryIndex = null;
            currentSubCategoryExpanded = true;
          }
        }
      } else {
        // This is a sub-item (line item)
        // Include if:
        // 1. We have a parent category and it's expanded, AND
        // 2. Either no sub-category exists, or sub-category is expanded
        if (parentCategoryIndex !== null && parentCategoryExpanded) {
          if (currentSubCategoryIndex === null || currentSubCategoryExpanded) {
            filtered.push({ ...item, originalIndex: index });
          }
        }
      }
    });

    return filtered;
  };

  // Format display value
  const formatDisplayValue = (value: string) => {
    if (!value || value === '') return '-';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  // Render editable cell component
  const renderEditableCell = (rowIdx: number, colIdx: number, sheetOffset: number = 0) => {
    const actualRowIdx = rowIdx + sheetOffset * 1000; // Offset to avoid conflicts between sheets
    const cellValue = getCellValue(actualRowIdx, colIdx);
    const isEditing = editingCell?.row === actualRowIdx && editingCell?.col === colIdx;
    const isDragging = draggingCell?.row === actualRowIdx && draggingCell?.col === colIdx;
    const isDragTarget = dragTarget?.row === actualRowIdx && dragTarget?.col === colIdx;
    
    return (
      <TableCell 
        key={`cell-${actualRowIdx}-${colIdx}`}
        align="right"
        sx={{ 
          cursor: 'pointer',
          position: 'relative',
          padding: '4px 8px',
          minWidth: 140,
          '&:hover': { backgroundColor: '#e0f2fe' },
          backgroundColor: isDragTarget ? '#dbeafe' : isDragging ? '#f0f9ff' : 'transparent',
        }}
        onClick={() => setEditingCell({ row: actualRowIdx, col: colIdx })}
        draggable={!!cellValue}
        onDragStart={(e) => handleDragStart(e, actualRowIdx, colIdx)}
        onDragOver={(e) => handleDragOver(e, actualRowIdx, colIdx)}
        onDrop={(e) => handleDrop(e, actualRowIdx, colIdx)}
        onDragEnd={handleDragEnd}
      >
        {isEditing ? (
          <TextField
            size="small"
            type="text"
            inputMode="numeric"
            autoFocus
            defaultValue={cellValue}
            onBlur={(e) => {
              // Clean and validate numeric input
              const cleaned = e.target.value.replace(/[^\d.-]/g, '').replace(/,/g, '');
              if (cleaned === '' || cleaned === '-') {
                handleCellEdit(actualRowIdx, colIdx, '');
              } else {
                const numValue = parseFloat(cleaned);
                if (!isNaN(numValue)) {
                  handleCellEdit(actualRowIdx, colIdx, numValue.toString());
                } else {
                  handleCellEdit(actualRowIdx, colIdx, '');
                }
              }
              setEditingCell(null);
            }}
            onChange={(e) => {
              // Only allow numeric characters, decimal point, and minus sign
              const value = e.target.value;
              const cleaned = value.replace(/[^\d.-]/g, '').replace(/,/g, '');
              e.target.value = cleaned;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const target = e.target as HTMLInputElement;
                const cleaned = target.value.replace(/[^\d.-]/g, '').replace(/,/g, '');
                if (cleaned === '' || cleaned === '-') {
                  handleCellEdit(actualRowIdx, colIdx, '');
                } else {
                  const numValue = parseFloat(cleaned);
                  if (!isNaN(numValue)) {
                    handleCellEdit(actualRowIdx, colIdx, numValue.toString());
                  } else {
                    handleCellEdit(actualRowIdx, colIdx, '');
                  }
                }
                setEditingCell(null);
              } else if (e.key === 'Escape') {
                setEditingCell(null);
              } else if (!/[0-9.-]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{ 
              width: 140,
              '& .MuiInputBase-root': {
                height: '32px',
                fontSize: '0.875rem',
              },
              '& .MuiInputBase-input': {
                padding: '4px 8px',
                height: '24px',
              }
            }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Typography variant="body2" sx={{ color: cellValue ? '#0f172a' : '#64748b', fontWeight: cellValue ? 500 : 400 }}>
              {formatDisplayValue(cellValue)}
            </Typography>
            {cellValue && (
              <ContentCopy sx={{ fontSize: 12, color: '#94a3b8', opacity: 0.6 }} />
            )}
          </Box>
        )}
      </TableCell>
    );
  };

  return (
    <Box sx={{ 
      p: 0, 
      mt: -18, // Counteract AppLayout's pt: 18
      mb: -4, // Counteract AppLayout's pb: 4
      mx: -4, // Counteract AppLayout's px: 4
      height: 'calc(100vh - 64px)', // Account for fixed header only
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden'
    }}>
      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0, px: 2, flexShrink: 0 }}>
        <Tabs value={activeMainTab} onChange={handleMainTabChange} aria-label="simulation tabs" sx={{ minHeight: 36 }}>
          <Tab label="Performance Simulation" icon={<Calculate />} iconPosition="start" />
          <Tab label="Projections" icon={<TrendingUp />} iconPosition="start" />
          <Tab label="Scenarios" icon={<ShowChart />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab Content - Performance Simulation */}
      {activeMainTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, px: 2, position: 'relative' }}>
          {/* Sub-Tabs for Performance Simulation */}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, mt: 10, pt: 2 }}>
            <Box sx={{ 
              borderBottom: 1, 
              borderColor: 'divider', 
              mb: 0.5, 
              backgroundColor: 'white', 
              position: 'sticky', 
              top: 0, 
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}>
              <Tabs 
                value={activeSubTab} 
                onChange={handleSubTabChange} 
                aria-label="sub-sheet tabs" 
                sx={{ minHeight: 40, flex: 1 }}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Aggregated P&L" />
                <Tab label="Revenue Breakdown" />
                <Tab label="Delivery Costs" />
                <Tab label="SG&A Expenses" />
                <Tab label="Finance & Forex Charges" />
              </Tabs>
              
              {/* Financial Year and Scenario Selector - In same row as sub-tabs */}
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                alignItems: 'center', 
                flexShrink: 0,
                pr: 1
              }}>
                {/* Financial Year Selector */}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="fy-select-label">Financial Year</InputLabel>
                    <Select
                      labelId="fy-select-label"
                      label="Financial Year"
                      value={selectedFY || ''}
                      onChange={(e) => handleFYChange(e.target.value)}
                      disabled={loadingFYs}
                      sx={{ 
                        backgroundColor: 'white',
                        '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem' }
                      }}
                    >
                      {availableFYs.length === 0 ? (
                        <MenuItem value={selectedFY || ''} disabled>
                          {loadingFYs ? 'Loading...' : (selectedFY || 'No FY available')}
                        </MenuItem>
                      ) : (
                        availableFYs.map((fy) => (
                          <MenuItem key={fy} value={fy}>{fy}</MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  <IconButton 
                    size="small" 
                    onClick={handleAddFY}
                    sx={{ 
                      border: '1px solid #e5e7eb',
                      backgroundColor: 'white',
                      '&:hover': { backgroundColor: '#f8fafc' },
                      width: 32,
                      height: 32
                    }}
                    title="Add Financial Year"
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Box>

                {/* Auto-save indicator */}
                {selectedScenario && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {saveStatus === 'saving' && (
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                        Saving...
                      </Typography>
                    )}
                    {saveStatus === 'saved' && (
                      <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.7rem' }}>
                        Saved
                      </Typography>
                    )}
                    {saveStatus === 'error' && (
                      <Typography variant="caption" sx={{ color: '#ef4444', fontSize: '0.7rem' }}>
                        Error
                      </Typography>
                    )}
                  </Box>
                )}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select
                    value={selectedScenario || ''}
                    displayEmpty
                    onChange={(e) => setSelectedScenario(e.target.value)}
                    disabled={loadingScenarios}
                    sx={{ 
                      backgroundColor: 'white',
                      '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem' }
                    }}
                  >
                    <MenuItem value="">-- Select Scenario --</MenuItem>
                    <MenuItem value="new">+ Create New Scenario</MenuItem>
                    {scenarios.map((scenario) => (
                      <MenuItem key={scenario.id} value={scenario.id.toString()}>
                        {scenario.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {scenarioError && (
                  <Typography variant="caption" sx={{ color: '#ef4444', fontSize: '0.7rem' }}>
                    {scenarioError}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Action buttons removed - auto-save is enabled */}

            {/* Aggregated P&L Tab (Read-Only, Expanded) */}
            {activeSubTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.875rem' }}>
                    Aggregated P&L (Auto-Calculated)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic', fontSize: '0.7rem' }}>
                    Read-only • Calculated from sub-sheets below
                  </Typography>
                </Box>
                <TableContainer sx={{ border: '1px solid #e5e7eb', borderRadius: 0, flex: 1, overflow: 'auto', mx: -2, px: 2 }}>
                  <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', position: 'sticky', left: 0, zIndex: 2, minWidth: 300, padding: '8px 12px', fontSize: '0.75rem' }}>
                              Item Name
                            </TableCell>
                            {months.map((month) => (
                              <TableCell key={month} align="right" sx={{ fontWeight: 600, backgroundColor: '#f8fafc', minWidth: 120, padding: '8px 12px', fontSize: '0.75rem' }}>
                                {month}
                              </TableCell>
                            ))}
                            <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc', minWidth: 120, padding: '8px 12px', fontSize: '0.75rem' }}>
                              YTD
                            </TableCell>
                          </TableRow>
                        </TableHead>
                    <TableBody>
                      {EXPANDED_PNL_STRUCTURE.map((item, idx) => (
                        <TableRow 
                          key={idx} 
                          sx={{ 
                            '&:hover': { backgroundColor: '#f8fafc' },
                            backgroundColor: item.isCalculated ? '#f0fdf4' : item.isTotal ? '#f1f5f9' : '#ffffff'
                          }}
                        >
                          <TableCell 
                            sx={{ 
                              fontWeight: item.isTotal ? 700 : item.isCalculated ? 600 : 500,
                              position: 'sticky',
                              left: 0,
                              backgroundColor: item.isCalculated ? '#f0fdf4' : item.isTotal ? '#f1f5f9' : '#ffffff',
                              borderRight: '1px solid #e5e7eb',
                              paddingLeft: `${16 + getIndentation(item.level)}px`
                            }}
                          >
                            {item.isTotal && '📊 '}
                            {item.isCalculated && '⚡ '}
                            {item.name}
                          </TableCell>
                          {months.map((month) => (
                            <TableCell key={month} align="right" sx={{ color: '#64748b' }}>
                              -
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: item.isTotal || item.isCalculated ? 600 : 500, color: '#0f172a' }}>
                            -
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

        {/* Revenue Breakdown Tab */}
        {activeSubTab === 1 && (() => {
          const sheetOffset = 1000;
          const hasUS = revenueItems.some(item => item.name === 'US' && item.isCategory);
          
          return (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {!hasUS && (
              <Box sx={{ p: 2, mb: 1, backgroundColor: '#fef3c7', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mx: 2, mt: 1, flexShrink: 0 }}>
                <Typography variant="body2" sx={{ color: '#92400e' }}>
                  US category and its items were deleted. Click to restore.
                </Typography>
                <Button 
                  size="small" 
                  variant="contained" 
                  onClick={restoreUSCategory}
                  sx={{ ml: 2, backgroundColor: '#f59e0b', '&:hover': { backgroundColor: '#d97706' } }}
                >
                  Restore US
                </Button>
              </Box>
            )}
          <TableContainer 
            ref={setTableContainerRef}
            sx={{ border: '1px solid #e5e7eb', borderRadius: 0, flex: 1, overflow: 'auto', mx: -2, px: 2 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', position: 'sticky', left: 0, zIndex: 3, minWidth: 250, borderRight: '1px solid #e5e7eb', padding: '6px 10px', fontSize: '0.75rem' }}>
                    Item Name
                  </TableCell>
                  {months.map((month) => (
                    <TableCell key={month} align="right" sx={{ fontWeight: 600, backgroundColor: '#f8fafc', minWidth: 120, padding: '6px 10px', fontSize: '0.75rem' }}>
                      {month}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc', minWidth: 120, padding: '6px 10px', fontSize: '0.75rem' }}>
                    YTD
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredItems(revenueItems, 'revenue').map((item, displayIdx) => {
                  const rowIdx = item.originalIndex;
                  const isExpanded = isCategoryExpanded('revenue', rowIdx);
                  return (
                  <TableRow 
                    key={rowIdx} 
                    sx={{ 
                      '&:hover': { backgroundColor: '#f8fafc' },
                      backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff'
                    }}
                  >
                    <TableCell 
                      sx={{ 
                        fontWeight: item.isCategory ? 700 : 500,
                        position: 'sticky',
                        left: 0,
                        backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff',
                        borderRight: '1px solid #e5e7eb',
                        zIndex: 2,
                        paddingLeft: item.isCategory ? '16px' : '32px' // Indent non-category items
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {item.isCategory && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCategoryExpand('revenue', rowIdx);
                            }}
                            sx={{ 
                              padding: '2px',
                              minWidth: 'auto',
                              width: '20px',
                              height: '20px',
                              '&:hover': { backgroundColor: '#e0f2fe' }
                            }}
                            title={isExpanded ? "Collapse" : "Expand"}
                          >
                            {isExpanded ? (
                              <ExpandMore sx={{ fontSize: 18 }} />
                            ) : (
                              <ChevronRight sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        )}
                        {item.isCategory && '📊 '}
                        {item.isCategory ? (
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item.name}
                          </Typography>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              cursor: 'text',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => {
                              const newName = prompt('Enter new name:', item.name);
                              if (newName && newName.trim()) {
                                handleEditRowName(rowIdx, newName.trim());
                              }
                            }}
                          >
                            {item.name}
                          </Typography>
                        )}
                        {item.isCategory && (
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ 
                              padding: '2px',
                              minWidth: 'auto',
                              width: '20px',
                              height: '20px',
                              '&:hover': { backgroundColor: '#e0f2fe' }
                            }}
                            title="Add row under this category"
                            onClick={() => handleAddRowUnderCategory(rowIdx)}
                          >
                            <Add sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                        <IconButton
                          disabled={can("delete")===false}
                          size="small"
                          color="error"
                          sx={{ 
                            padding: '2px',
                            minWidth: 'auto',
                            width: '20px',
                            height: '20px',
                            '&:hover': { backgroundColor: '#fee2e2' }
                          }}
                          title={item.isCategory ? "Delete category and all items under it" : "Delete row"}
                          onClick={() => handleRemoveRow(rowIdx)}
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    {months.map((month, colIdx) => renderEditableCell(rowIdx + sheetOffset, colIdx, sheetOffset))}
                    <TableCell align="right" sx={{ fontWeight: item.isCategory ? 700 : 600, color: '#0f172a' }}>
                      {(() => {
                        if (item.isCategory) {
                          // Calculate total for all items under this category until next category
                          let total = 0;
                          let nextIdx = rowIdx + 1;
                          
                          // Sum all items until we hit the next category or end of list
                          while (nextIdx < revenueItems.length && !revenueItems[nextIdx]?.isCategory) {
                            for (let colIdx = 0; colIdx < months.length; colIdx++) {
                              const val = getCellValue(nextIdx + sheetOffset, colIdx);
                              total += (parseFloat(val) || 0);
                            }
                            nextIdx++;
                          }
                          return formatDisplayValue(total.toString());
                        } else {
                          // Regular item total
                          const total = months.reduce((sum, _, colIdx) => {
                            const val = getCellValue(rowIdx + sheetOffset, colIdx);
                            return sum + (parseFloat(val) || 0);
                          }, 0);
                          return formatDisplayValue(total.toString());
                        }
                      })()}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          );
        })()}

        {/* Cost of Sales Tab */}
        {activeSubTab === 2 && (() => {
          const sheetOffset = 2000;
          return (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <TableContainer 
            ref={setTableContainerRef}
            sx={{ border: '1px solid #e5e7eb', borderRadius: 0, flex: 1, overflow: 'auto', mx: -2, px: 2 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', position: 'sticky', left: 0, zIndex: 3, minWidth: 250, borderRight: '1px solid #e5e7eb', padding: '6px 10px', fontSize: '0.75rem' }}>
                    Item Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', position: 'sticky', left: 250, zIndex: 3, minWidth: 150, borderRight: '1px solid #e5e7eb', padding: '6px 10px', fontSize: '0.75rem' }}>
                    Department
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', position: 'sticky', left: 400, zIndex: 3, minWidth: 150, borderRight: '1px solid #e5e7eb', padding: '6px 10px', fontSize: '0.75rem' }}>
                    Source
                  </TableCell>
                  {months.map((month) => (
                    <TableCell key={month} align="right" sx={{ fontWeight: 600, backgroundColor: '#f8fafc', minWidth: 120, padding: '6px 10px', fontSize: '0.75rem' }}>
                      {month}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc', minWidth: 120, padding: '6px 10px', fontSize: '0.75rem' }}>
                    YTD
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredItems(costOfSalesItems, 'cost_of_sales').map((item, displayIdx) => {
                  const rowIdx = item.originalIndex;
                  const rowKey = `cost-${rowIdx}`;
                  const currentSource = rowSources[rowKey] || 'Cashflow';
                  const currentDepartment = rowDepartments[rowKey] || '';
                  const isExpanded = isCategoryExpanded('cost_of_sales', rowIdx);
                  return (
                  <TableRow 
                    key={rowIdx} 
                    sx={{ 
                      '&:hover': { backgroundColor: '#f8fafc' },
                      backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff'
                    }}
                  >
                    <TableCell 
                      sx={{ 
                        fontWeight: item.isCategory ? 700 : 500,
                        position: 'sticky',
                        left: 0,
                        backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff',
                        borderRight: '1px solid #e5e7eb',
                        zIndex: 2,
                        paddingLeft: item.isCategory ? '16px' : '32px' // Indent non-category items
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {item.isCategory && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCategoryExpand('cost_of_sales', rowIdx);
                            }}
                            sx={{ 
                              padding: '2px',
                              minWidth: 'auto',
                              width: '20px',
                              height: '20px',
                              '&:hover': { backgroundColor: '#e0f2fe' }
                            }}
                            title={isExpanded ? "Collapse" : "Expand"}
                          >
                            {isExpanded ? (
                              <ExpandMore sx={{ fontSize: 18 }} />
                            ) : (
                              <ChevronRight sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        )}
                        {item.isCategory && '📊 '}
                        {item.isCategory ? (
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item.name}
                          </Typography>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              cursor: 'text',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => {
                              const newName = prompt('Enter new name:', item.name);
                              if (newName && newName.trim()) {
                                handleEditRowName(rowIdx, newName.trim());
                              }
                            }}
                          >
                            {item.name}
                          </Typography>
                        )}
                        {item.isCategory && (
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ 
                              padding: '2px',
                              minWidth: 'auto',
                              width: '20px',
                              height: '20px',
                              '&:hover': { backgroundColor: '#e0f2fe' }
                            }}
                            title="Add row under this category"
                            onClick={() => handleAddRowUnderCategory(rowIdx)}
                          >
                            <Add sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                        <IconButton
                          disabled={can("delete")===false}
                          size="small"
                          color="error"
                          sx={{ 
                            padding: '2px',
                            minWidth: 'auto',
                            width: '20px',
                            height: '20px',
                            '&:hover': { backgroundColor: '#fee2e2' }
                          }}
                          title={item.isCategory ? "Delete category and all items under it" : "Delete row"}
                          onClick={() => handleRemoveRow(rowIdx)}
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    {/* Department Column - Only for sub-rows */}
                    <TableCell 
                      sx={{ 
                        position: 'sticky',
                        left: 250,
                        backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff',
                        borderRight: '1px solid #e5e7eb',
                        zIndex: 2,
                        padding: '4px 8px'
                      }}
                    >
                      {!item.isCategory ? (
                        <Select
                          value={currentDepartment}
                          onChange={(e) => {
                            setRowDepartments({ ...rowDepartments, [rowKey]: e.target.value });
                            triggerAutoSave();
                          }}
                          size="small"
                          displayEmpty
                          sx={{ 
                            fontSize: '0.75rem',
                            height: '28px',
                            '& .MuiSelect-select': { py: 0.5, px: 1 },
                            minWidth: 140
                          }}
                        >
                          <MenuItem value="">Select Department</MenuItem>
                          <MenuItem value="Delivery">Delivery</MenuItem>
                          <MenuItem value="Sales">Sales</MenuItem>
                          <MenuItem value="Marketing">Marketing</MenuItem>
                          <MenuItem value="HR">HR</MenuItem>
                          <MenuItem value="Admin Finance">Admin Finance</MenuItem>
                        </Select>
                      ) : (
                        <Box sx={{ width: 140, height: 28 }} />
                      )}
                    </TableCell>
                    {/* Source Column - Only for sub-rows */}
                    <TableCell 
                      sx={{ 
                        position: 'sticky',
                        left: 400,
                        backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff',
                        borderRight: '1px solid #e5e7eb',
                        zIndex: 2,
                        padding: '4px 8px'
                      }}
                    >
                      {!item.isCategory ? (
                        <Select
                          value={currentSource}
                          onChange={(e) => {
                            setRowSources({ ...rowSources, [rowKey]: e.target.value as 'Cashflow' | 'Investment' });
                            triggerAutoSave();
                          }}
                          size="small"
                          sx={{ 
                            fontSize: '0.75rem',
                            height: '28px',
                            '& .MuiSelect-select': { py: 0.5, px: 1 },
                            minWidth: 120
                          }}
                        >
                          <MenuItem value="Cashflow">Cashflow</MenuItem>
                          <MenuItem value="Investment">Investment</MenuItem>
                        </Select>
                      ) : (
                        <Box sx={{ width: 120, height: 28 }} />
                      )}
                    </TableCell>
                    {months.map((month, colIdx) => renderEditableCell(rowIdx + sheetOffset, colIdx, sheetOffset))}
                    <TableCell align="right" sx={{ fontWeight: item.isCategory ? 700 : 600, color: '#0f172a' }}>
                      {(() => {
                        if (item.isCategory) {
                          // Calculate total for all items under this category until next category
                          let total = 0;
                          let nextIdx = rowIdx + 1;
                          
                          // Sum all items until we hit the next category or end of list
                          while (nextIdx < costOfSalesItems.length && !costOfSalesItems[nextIdx]?.isCategory) {
                            for (let colIdx = 0; colIdx < months.length; colIdx++) {
                              const val = getCellValue(nextIdx + sheetOffset, colIdx);
                              total += (parseFloat(val) || 0);
                            }
                            nextIdx++;
                          }
                          return formatDisplayValue(total.toString());
                        } else {
                          // Regular item total
                          const total = months.reduce((sum, _, colIdx) => {
                            const val = getCellValue(rowIdx + sheetOffset, colIdx);
                            return sum + (parseFloat(val) || 0);
                          }, 0);
                          return formatDisplayValue(total.toString());
                        }
                      })()}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          );
        })()}

        {/* SG&A Expenses Tab */}
        {activeSubTab === 3 && (() => {
          const sheetOffset = 3000;
          return (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <TableContainer 
            ref={setTableContainerRef}
            sx={{ border: '1px solid #e5e7eb', borderRadius: 0, flex: 1, overflow: 'auto', mx: -2, px: 2 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', position: 'sticky', left: 0, zIndex: 3, minWidth: 250, borderRight: '1px solid #e5e7eb', padding: '6px 10px', fontSize: '0.75rem' }}>
                    Item Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', position: 'sticky', left: 250, zIndex: 3, minWidth: 150, borderRight: '1px solid #e5e7eb', padding: '6px 10px', fontSize: '0.75rem' }}>
                    Department
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', position: 'sticky', left: 400, zIndex: 3, minWidth: 150, borderRight: '1px solid #e5e7eb', padding: '6px 10px', fontSize: '0.75rem' }}>
                    Source
                  </TableCell>
                  {months.map((month) => (
                    <TableCell key={month} align="right" sx={{ fontWeight: 600, backgroundColor: '#f8fafc', minWidth: 120, padding: '6px 10px', fontSize: '0.75rem' }}>
                      {month}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc', minWidth: 120, padding: '6px 10px', fontSize: '0.75rem' }}>
                    YTD
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredItems(sgaItems, 'sga').map((item, displayIdx) => {
                  const rowIdx = item.originalIndex;
                  const rowKey = `sga-${rowIdx}`;
                  const currentSource = rowSources[rowKey] || 'Cashflow';
                  const currentDepartment = rowDepartments[rowKey] || '';
                  const isExpanded = isCategoryExpanded('sga', rowIdx);
                  return (
                  <TableRow 
                    key={rowIdx} 
                    sx={{ 
                      '&:hover': { backgroundColor: '#f8fafc' },
                      backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff'
                    }}
                  >
                    <TableCell 
                      sx={{ 
                        fontWeight: item.isCategory ? 700 : 500,
                        position: 'sticky',
                        left: 0,
                        backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff',
                        borderRight: '1px solid #e5e7eb',
                        zIndex: 2,
                        paddingLeft: (() => {
                          // Determine indentation based on hierarchy
                          const knownSubCategories = ['Sales Staff Costs', 'Marketing Staff Costs', 'HR Staff Costs', 'Finance & Admin Staff Costs'];
                          if (item.isCategory) {
                            if (item.name === 'Corporate staff cost') {
                              return '16px'; // Top-level category
                            } else if (knownSubCategories.includes(item.name)) {
                              return '40px'; // Sub-category (indented under Corporate staff cost)
                            } else {
                              return '16px'; // Other top-level categories
                            }
                          } else {
                            return '64px'; // Line items (indented under sub-categories)
                          }
                        })()
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {item.isCategory && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCategoryExpand('sga', rowIdx);
                            }}
                            sx={{ 
                              padding: '2px',
                              minWidth: 'auto',
                              width: '20px',
                              height: '20px',
                              '&:hover': { backgroundColor: '#e0f2fe' }
                            }}
                            title={isExpanded ? "Collapse" : "Expand"}
                          >
                            {isExpanded ? (
                              <ExpandMore sx={{ fontSize: 18 }} />
                            ) : (
                              <ChevronRight sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        )}
                        {item.isCategory && '📊 '}
                        {item.isCategory ? (
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item.name}
                          </Typography>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              cursor: 'text',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => {
                              const newName = prompt('Enter new name:', item.name);
                              if (newName && newName.trim()) {
                                handleEditRowName(rowIdx, newName.trim());
                              }
                            }}
                          >
                            {item.name}
                          </Typography>
                        )}
                        {item.isCategory && (
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ 
                              padding: '2px',
                              minWidth: 'auto',
                              width: '20px',
                              height: '20px',
                              '&:hover': { backgroundColor: '#e0f2fe' }
                            }}
                            title="Add row under this category"
                            onClick={() => handleAddRowUnderCategory(rowIdx)}
                          >
                            <Add sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                        <IconButton
                          disabled={can("delete")===false}
                          size="small"
                          color="error"
                          sx={{ 
                            padding: '2px',
                            minWidth: 'auto',
                            width: '20px',
                            height: '20px',
                            '&:hover': { backgroundColor: '#fee2e2' }
                          }}
                          title={item.isCategory ? "Delete category and all items under it" : "Delete row"}
                          onClick={() => handleRemoveRow(rowIdx)}
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    {/* Department Column - Only for sub-rows */}
                    <TableCell 
                      sx={{ 
                        position: 'sticky',
                        left: 250,
                        backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff',
                        borderRight: '1px solid #e5e7eb',
                        zIndex: 2,
                        padding: '4px 8px'
                      }}
                    >
                      {!item.isCategory ? (
                        <Select
                          value={currentDepartment}
                          onChange={(e) => {
                            setRowDepartments({ ...rowDepartments, [rowKey]: e.target.value });
                            triggerAutoSave();
                          }}
                          size="small"
                          displayEmpty
                          sx={{ 
                            fontSize: '0.75rem',
                            height: '28px',
                            '& .MuiSelect-select': { py: 0.5, px: 1 },
                            minWidth: 140
                          }}
                        >
                          <MenuItem value="">Select Department</MenuItem>
                          <MenuItem value="Delivery">Delivery</MenuItem>
                          <MenuItem value="Sales">Sales</MenuItem>
                          <MenuItem value="Marketing">Marketing</MenuItem>
                          <MenuItem value="HR">HR</MenuItem>
                          <MenuItem value="Admin Finance">Admin Finance</MenuItem>
                        </Select>
                      ) : (
                        <Box sx={{ width: 140, height: 28 }} />
                      )}
                    </TableCell>
                    {/* Source Column - Only for sub-rows */}
                    <TableCell 
                      sx={{ 
                        position: 'sticky',
                        left: 400,
                        backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff',
                        borderRight: '1px solid #e5e7eb',
                        zIndex: 2,
                        padding: '4px 8px'
                      }}
                    >
                      {!item.isCategory ? (
                        <Select
                          value={currentSource}
                          onChange={(e) => {
                            setRowSources({ ...rowSources, [rowKey]: e.target.value as 'Cashflow' | 'Investment' });
                            triggerAutoSave();
                          }}
                          size="small"
                          sx={{ 
                            fontSize: '0.75rem',
                            height: '28px',
                            '& .MuiSelect-select': { py: 0.5, px: 1 },
                            minWidth: 120
                          }}
                        >
                          <MenuItem value="Cashflow">Cashflow</MenuItem>
                          <MenuItem value="Investment">Investment</MenuItem>
                        </Select>
                      ) : (
                        <Box sx={{ width: 120, height: 28 }} />
                      )}
                    </TableCell>
                    {months.map((month, colIdx) => renderEditableCell(rowIdx + sheetOffset, colIdx, sheetOffset))}
                    <TableCell align="right" sx={{ fontWeight: item.isCategory ? 700 : 600, color: '#0f172a' }}>
                      {(() => {
                        if (item.isCategory) {
                          // Calculate total for all items under this category until next category
                          let total = 0;
                          let nextIdx = rowIdx + 1;
                          
                          // Sum all items until we hit the next category or end of list
                          while (nextIdx < sgaItems.length && !sgaItems[nextIdx]?.isCategory) {
                            for (let colIdx = 0; colIdx < months.length; colIdx++) {
                              const val = getCellValue(nextIdx + sheetOffset, colIdx);
                              total += (parseFloat(val) || 0);
                            }
                            nextIdx++;
                          }
                          return formatDisplayValue(total.toString());
                        } else {
                          // Regular item total
                          const total = months.reduce((sum, _, colIdx) => {
                            const val = getCellValue(rowIdx + sheetOffset, colIdx);
                            return sum + (parseFloat(val) || 0);
                          }, 0);
                          return formatDisplayValue(total.toString());
                        }
                      })()}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          );
        })()}

        {/* Finance & Forex Charges Tab */}
        {activeSubTab === 4 && (() => {
          const sheetOffset = 4000;
          return (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <TableContainer 
            ref={setTableContainerRef}
            sx={{ border: '1px solid #e5e7eb', borderRadius: 0, flex: 1, overflow: 'auto', mx: -2, px: 2 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', position: 'sticky', left: 0, zIndex: 3, minWidth: 250, borderRight: '1px solid #e5e7eb', padding: '6px 10px', fontSize: '0.75rem' }}>
                    Item Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', position: 'sticky', left: 250, zIndex: 3, minWidth: 150, borderRight: '1px solid #e5e7eb', padding: '6px 10px', fontSize: '0.75rem' }}>
                    Source
                  </TableCell>
                  {months.map((month) => (
                    <TableCell key={month} align="right" sx={{ fontWeight: 600, backgroundColor: '#f8fafc', minWidth: 120, padding: '6px 10px', fontSize: '0.75rem' }}>
                      {month}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc', minWidth: 120, padding: '6px 10px', fontSize: '0.75rem' }}>
                    YTD
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredItems(financeItems, 'finance').map((item, displayIdx) => {
                  const rowIdx = item.originalIndex;
                  const rowKey = `finance-${rowIdx}`;
                  const currentSource = rowSources[rowKey] || 'Cashflow';
                  const isExpanded = isCategoryExpanded('finance', rowIdx);
                  return (
                  <TableRow 
                    key={rowIdx} 
                    sx={{ 
                      '&:hover': { backgroundColor: '#f8fafc' },
                      backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff'
                    }}
                  >
                    <TableCell 
                      sx={{ 
                        fontWeight: item.isCategory ? 700 : 500,
                        position: 'sticky',
                        left: 0,
                        backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff',
                        borderRight: '1px solid #e5e7eb',
                        zIndex: 2,
                        paddingLeft: item.isCategory ? '16px' : '32px' // Indent non-category items
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {item.isCategory && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCategoryExpand('finance', rowIdx);
                            }}
                            sx={{ 
                              padding: '2px',
                              minWidth: 'auto',
                              width: '20px',
                              height: '20px',
                              '&:hover': { backgroundColor: '#e0f2fe' }
                            }}
                            title={isExpanded ? "Collapse" : "Expand"}
                          >
                            {isExpanded ? (
                              <ExpandMore sx={{ fontSize: 18 }} />
                            ) : (
                              <ChevronRight sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        )}
                        {item.isCategory && '📊 '}
                        {item.isCategory ? (
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item.name}
                          </Typography>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              cursor: 'text',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => {
                              const newName = prompt('Enter new name:', item.name);
                              if (newName && newName.trim()) {
                                handleEditRowName(rowIdx, newName.trim());
                              }
                            }}
                          >
                            {item.name}
                          </Typography>
                        )}
                        {item.isCategory && (
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ 
                              padding: '2px',
                              minWidth: 'auto',
                              width: '20px',
                              height: '20px',
                              '&:hover': { backgroundColor: '#e0f2fe' }
                            }}
                            title="Add row under this category"
                            onClick={() => handleAddRowUnderCategory(rowIdx)}
                          >
                            <Add sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          disabled={can("delete")===false}
                          sx={{ 
                            padding: '2px',
                            minWidth: 'auto',
                            width: '20px',
                            height: '20px',
                            '&:hover': { backgroundColor: '#fee2e2' }
                          }}
                          title={item.isCategory ? "Delete category and all items under it" : "Delete row"}
                          onClick={() => handleRemoveRow(rowIdx)}
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    {/* Source Column - Only for sub-rows */}
                    <TableCell 
                      sx={{ 
                        position: 'sticky',
                        left: 250,
                        backgroundColor: item.isCategory ? '#f1f5f9' : '#ffffff',
                        borderRight: '1px solid #e5e7eb',
                        zIndex: 2,
                        padding: '4px 8px'
                      }}
                    >
                      {!item.isCategory ? (
                        <Select
                          value={currentSource}
                          onChange={(e) => {
                            setRowSources({ ...rowSources, [rowKey]: e.target.value as 'Cashflow' | 'Investment' });
                            triggerAutoSave();
                          }}
                          size="small"
                          sx={{ 
                            fontSize: '0.75rem',
                            height: '28px',
                            '& .MuiSelect-select': { py: 0.5, px: 1 },
                            minWidth: 120
                          }}
                        >
                          <MenuItem value="Cashflow">Cashflow</MenuItem>
                          <MenuItem value="Investment">Investment</MenuItem>
                        </Select>
                      ) : (
                        <Box sx={{ width: 120, height: 28 }} />
                      )}
                    </TableCell>
                    {months.map((month, colIdx) => renderEditableCell(rowIdx + sheetOffset, colIdx, sheetOffset))}
                    <TableCell align="right" sx={{ fontWeight: item.isCategory ? 700 : 600, color: '#0f172a' }}>
                      {(() => {
                        if (item.isCategory) {
                          // Calculate total for all items under this category until next category
                          let total = 0;
                          let nextIdx = rowIdx + 1;
                          
                          // Sum all items until we hit the next category or end of list
                          while (nextIdx < financeItems.length && !financeItems[nextIdx]?.isCategory) {
                            for (let colIdx = 0; colIdx < months.length; colIdx++) {
                              const val = getCellValue(nextIdx + sheetOffset, colIdx);
                              total += (parseFloat(val) || 0);
                            }
                            nextIdx++;
                          }
                          return formatDisplayValue(total.toString());
                        } else {
                          // Regular item total
                          const total = months.reduce((sum, _, colIdx) => {
                            const val = getCellValue(rowIdx + sheetOffset, colIdx);
                            return sum + (parseFloat(val) || 0);
                          }, 0);
                          return formatDisplayValue(total.toString());
                        }
                      })()}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          );
        })()}
      </Box>
    </Box>
  )}

  {/* Tab Content - Projections */}
  {activeMainTab === 1 && (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Projections
        </Typography>
        <Typography variant="body1" sx={{ color: '#64748b' }}>
          Financial projections feature will be implemented here.
        </Typography>
      </Paper>
    </Box>
  )}

  {/* Tab Content - Scenarios */}
  {activeMainTab === 2 && (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Scenario Management
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Scenario</InputLabel>
              <Select
                value={selectedScenario}
                label="Select Scenario"
                onChange={(e) => setSelectedScenario(e.target.value)}
              >
                <MenuItem value="">-- Select Scenario --</MenuItem>
                <MenuItem value="new">+ Create New Scenario</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                disabled={can("write")===false}
                variant="outlined"
                startIcon={<Add />}
                size="small"
                onClick={() => setSelectedScenario('new')}
              >
                Create New
              </Button>
              <Button
                variant="outlined"
                startIcon={<Upload />}
                size="small"
              >
                Import Excel
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                size="small"
                disabled={!selectedScenario}
              >
                Save Scenario
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                size="small"
                disabled={!selectedScenario}
              >
                Export Excel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Master Data Upload (Coming Soon)
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
          Upload and manage master data sheets that will be used across all scenarios. This feature will allow you to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, color: '#64748b' }}>
          <li>Upload Revenue Breakdown sheets</li>
          <li>Upload Delivery Costs data</li>
          <li>Upload SG&A Expenses data</li>
          <li>Upload Finance & Forex Charges data</li>
          <li>Map and validate data before saving to database</li>
        </Box>
      </Paper>
    </Box>
  )}

      {/* Add Row Dialog */}
      <Dialog 
        open={addRowDialogOpen} 
        onClose={handleCancelAddRow}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
    <DialogTitle>Add New Item</DialogTitle>
    <DialogContent>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <FormControl component="fieldset">
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Select Type:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={isAddingHeader ? "contained" : "outlined"}
              onClick={() => setIsAddingHeader(true)}
              sx={{ flex: 1 }}
            >
              Sub Header
            </Button>
            <Button
              variant={!isAddingHeader ? "contained" : "outlined"}
              onClick={() => setIsAddingHeader(false)}
              sx={{ flex: 1 }}
            >
              Sub Row
            </Button>
          </Box>
          <Typography variant="caption" sx={{ mt: 1, color: '#64748b', display: 'block' }}>
            {isAddingHeader 
              ? "Creates a new section (like 'US', 'India'). Items below will be summarized to this header."
              : "Adds a regular item under the current category."}
          </Typography>
        </FormControl>
        <TextField
          autoFocus
          margin="dense"
          label={isAddingHeader ? "Header Name" : "Item Name"}
          fullWidth
          variant="outlined"
          value={newRowName}
          onChange={(e) => setNewRowName(e.target.value)}
          placeholder={isAddingHeader ? "e.g., US, India, Q1 2025" : "Enter item name"}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newRowName.trim()) {
              handleConfirmAddRow();
            }
          }}
        />
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={handleCancelAddRow}>Cancel</Button>
      <Button 
        onClick={handleConfirmAddRow} 
        variant="contained"
        disabled={!newRowName.trim() || can("write")===false}
      >
        Add {isAddingHeader ? 'Header' : 'Row'}
      </Button>
    </DialogActions>
  </Dialog>

      {/* Add Financial Year Dialog */}
      <Dialog 
        open={addFYDialogOpen} 
        onClose={() => {
          setAddFYDialogOpen(false);
          setNewFYYear('');
          setNewFYStartYear('');
          setNewFYEndYear('');
          setScenarioError(null);
        }}
      >
        <DialogTitle>Add New Financial Year</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: 400 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Financial Year"
              fullWidth
              variant="outlined"
              value={newFYYear}
              onChange={(e) => {
                const value = e.target.value;
                setNewFYYear(value);
                // Auto-populate start and end years if format is correct
                const match = value.match(/^FY(\d{4})-(\d{2})$/);
                if (match) {
                  setNewFYStartYear(match[1]);
                  setNewFYEndYear('20' + match[2]);
                }
              }}
              placeholder="FY2026-27"
              helperText="Format: FY2026-27 (Apr 2026 to Mar 2027)"
            />
            <TextField
              margin="dense"
              label="Start Year"
              fullWidth
              variant="outlined"
              type="number"
              value={newFYStartYear}
              onChange={(e) => setNewFYStartYear(e.target.value)}
              placeholder="2026"
            />
            <TextField
              margin="dense"
              label="End Year"
              fullWidth
              variant="outlined"
              type="number"
              value={newFYEndYear}
              onChange={(e) => setNewFYEndYear(e.target.value)}
              placeholder="2027"
            />
            {scenarioError && (
              <Typography variant="caption" sx={{ color: '#ef4444', mt: 1 }}>
                {scenarioError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddFYDialogOpen(false);
            setNewFYYear('');
            setNewFYStartYear('');
            setNewFYEndYear('');
            setScenarioError(null);
          }}>Cancel</Button>
          <Button 
            onClick={handleConfirmAddFY} 
            variant="contained"
            disabled={!newFYYear.trim()}
          >
            Add FY
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Financial Year Dialog */}
      <Dialog 
        open={changeFYDialogOpen} 
        onClose={() => {
          setChangeFYDialogOpen(false);
          setNewFYToSet('');
          setCopyFromFY('');
        }}
      >
        <DialogTitle>Change Financial Year</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: 400 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Changing to: <strong>{newFYToSet}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
              Would you like to copy data from another Financial Year? If not, the new FY will start empty.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Copy Data From (Optional)</InputLabel>
              <Select
                value={copyFromFY}
                onChange={(e) => setCopyFromFY(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">-- Don't Copy --</MenuItem>
                {availableFYs.filter(fy => fy !== newFYToSet).map((fy) => (
                  <MenuItem key={fy} value={fy}>{fy}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {scenarioError && (
              <Typography variant="caption" sx={{ color: '#ef4444', mt: 1 }}>
                {scenarioError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setChangeFYDialogOpen(false);
            setNewFYToSet('');
            setCopyFromFY('');
          }}>Cancel</Button>
          <Button 
            onClick={handleConfirmFYChange} 
            variant="contained"
          >
            Change FY
          </Button>
        </DialogActions>
      </Dialog>
</Box>
  );
};

export default ProjectionSimulationPage;
